// CanvasEditor.jsx
import React, { useEffect, useRef, useState } from "react";
import { StyleEditor } from "./StyleEditor";
import { PropertyEditor } from "./PropertyEditor";
// import { JsonEditor } from "./JsonEditor"; // <--- Import JsonEditor
import { createElement } from "./createElement";
import { useCanvasFirebase } from "../hooks/useCanvasFirebase";
import { Toolbar } from "./ToolBar";
import CanvasDisplay from "./canvasDisplay";
import { JsonEditor } from "../components/JsonEditor";
// === Configuration Flag ===
// Set to true to enable the JSON editor toggle, false to hide it.
// In a real application, this might come from environment variables,
// user roles, or a Firebase Remote Config setting.
const ENABLE_JSON_EDITOR_FOR_ADMINS = true;

export default function CanvasEditor({ slideId }) {
  const canvasRef = useRef(null);
  const [selectedId, setSelectedId] = useState(null);
  const [showJsonEditor, setShowJsonEditor] = useState(false); // <--- New state for editor mode

  const { elements, setElements, loading } = useCanvasFirebase(slideId, true);

  if (!slideId) return <p>Select a slide</p>;
  if (loading) return <p>Loading canvas…</p>;

  function handleDragEnd({ active, delta }) {
    setElements((prev) =>
      prev.map((el) =>
        el.id === active.id
          ? {
              ...el,
              layout: {
                ...el.layout,
                x: el.layout.x + delta.x,
                y: el.layout.y + delta.y,
              },
            }
          : el,
      ),
    );
  }

  function handleResize({ id, x, y, width, height }) {
    setElements((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, layout: { ...item.layout, x, y, width, height } }
          : item,
      ),
    );
  }

  function addText() {
    const rect = canvasRef.current.getBoundingClientRect();
    const el = createElement({
      type: "text",
      canvasRect: rect,
      elements,
    });

    setElements((prev) => [...prev, el]);
    setSelectedId(el.id);
  }

  function addImage() {
    const rect = canvasRef.current.getBoundingClientRect();
    const el = createElement({
      type: "image",
      canvasRect: rect,
      elements,
      payload: {
        src: "https://cdn-icons-png.freepik.com/256/364/364015.png",
      },
    });

    setElements((prev) => [...prev, el]);
    setSelectedId(el.id);
  }

  const handleCanvasPointerDown = (e) => {
    if (e.target === e.currentTarget) setSelectedId(null);
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-around",
        flexGrow: "1",
        background: "grey",
      }}
    >
      <Toolbar onAddText={addText} onAddImage={addImage} />

      {/* CANVAS for EDITING */}
      <CanvasDisplay
        elements={elements}
        canvasRef={canvasRef}
        canvasStyle={canvasStyle}
        selectedId={selectedId}
        onSelect={setSelectedId}
        onResize={handleResize}
        onDragEnd={handleDragEnd}
        onCanvasPointerDown={handleCanvasPointerDown}
        isEditable={true} // This is the editor, so it's editable
      />

      {/* COMBINED RIGHT SIDEBAR */}
      <div style={sidebarStyle}>
        {/* Editor Mode Toggle (only if enabled by flag) */}
        {ENABLE_JSON_EDITOR_FOR_ADMINS && (
          <div style={editorToggleStyles.container}>
            <button
              style={{
                ...editorToggleStyles.button,
                backgroundColor: !showJsonEditor ? "#3B00FF" : "#ccc",
              }}
              onClick={() => setShowJsonEditor(false)}
            >
              Visual Editor
            </button>
            <button
              style={{
                ...editorToggleStyles.button,
                backgroundColor: showJsonEditor ? "#3B00FF" : "#ccc",
              }}
              onClick={() => setShowJsonEditor(true)}
            >
              JSON Editor
            </button>
          </div>
        )}

        {/* Conditionally render editors */}
        {showJsonEditor ? (
          <JsonEditor
            elements={elements}
            onElementsChange={setElements} // JsonEditor will update elements directly
          />
        ) : (
          <>
            {/* Property Editor */}
            <PropertyEditor
              element={elements.find((e) => e.id === selectedId)}
              onChange={(patch) =>
                setElements((prev) =>
                  prev.map((el) =>
                    el.id === selectedId
                      ? { ...el, content: { ...el.content, ...patch } }
                      : el,
                  ),
                )
              }
            />

            {/* Visual Divider */}
            <div
              style={{ height: "1px", background: "rgba(255,255,255,0.1)" }}
            />

            {/* Style Editor */}
            <StyleEditor
              element={elements.find((e) => e.id === selectedId)}
              onChange={(style) =>
                setElements((prev) =>
                  prev.map((el) =>
                    el.id === selectedId
                      ? { ...el, style: { ...el.style, ...style } }
                      : el,
                  ),
                )
              }
            />
          </>
        )}
      </div>
    </div>
  );
}

// Add these styles for the sidebar and editor toggle
const sidebarStyle = {
  width: 280,
  display: "flex",
  flexDirection: "column",
  borderLeft: "1px solid #ddd",
  background: "#bebdbd", // Changed background for better contrast
  height: "calc(100vh - 56px)",
  overflowY: "auto",
};

const canvasStyle = {
  position: "relative",
  width: "360px",
  background: "#ffffff",
  margin: "0 auto",
  overflowY: "auto",
  height: "calc(100vh - 56px)",
  overflowX: "hidden",
};

const editorToggleStyles = {
  container: {
    display: "flex",
    justifyContent: "space-around",
    padding: "10px",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    marginBottom: "10px",
  },
  button: {
    flex: 1,
    margin: "0 5px",
    padding: "8px 5px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    color: "white",
    fontSize: "0.85rem",
    fontWeight: "bold",
  },
};
